import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { ArrowLeft, Copy, Plus, X, Trash, MapPin, PencilSimple, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, adminLogout } from "@/lib/api";
import { pinIcon } from "@/lib/map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 15, { duration: 1 });
  }, [position, map]);
  return null;
}

export default function ClientEditor() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [masters, setMasters] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [categories, setCategories] = useState([]);
  const [catInput, setCatInput] = useState("");
  const [editingStopId, setEditingStopId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [focus, setFocus] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: m }] = await Promise.all([
        api.get(`/admin/clients/${clientId}`),
        api.get("/admin/showrooms"),
      ]);
      setClient(c);
      setMasters(m);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminLogout();
        navigate("/");
      } else if (err?.response?.status === 404) {
        toast.error("Client not found");
        navigate("/admin");
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const available = useMemo(() => {
    if (!client) return [];
    const inTour = new Set(client.showrooms.map((s) => s.id));
    return masters.filter((m) => !inTour.has(m.id));
  }, [client, masters]);

  const selected = useMemo(
    () => masters.find((m) => m.id === selectedId) || null,
    [masters, selectedId]
  );

  const editingStop = useMemo(
    () => client?.showrooms.find((s) => s.id === editingStopId) || null,
    [client, editingStopId]
  );

  const shownShowroom = editingStop || selected;

  const addCategory = () => {
    const v = catInput.trim();
    if (!v) return;
    if (!categories.includes(v)) setCategories((c) => [...c, v]);
    setCatInput("");
  };

  const resetForm = () => {
    setSelectedId("");
    setCategories([]);
    setCatInput("");
    setEditingStopId(null);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editingStopId && !selectedId) return toast.error("Choose a showroom from the dropdown first");
    setSaving(true);
    try {
      if (editingStopId) {
        await api.put(`/admin/clients/${clientId}/stops/${editingStopId}`, { categories });
        toast.success("Checklist updated");
      } else {
        await api.post(`/admin/clients/${clientId}/stops`, {
          showroom_id: selectedId,
          categories,
        });
        toast.success("Showroom added to tour");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const editStop = (s) => {
    setEditingStopId(s.id);
    setSelectedId(s.id);
    setCategories(s.categories || []);
    setCatInput("");
    setFocus([s.lat, s.lng]);
  };

  const removeStop = async (s) => {
    if (!window.confirm(`Remove "${s.name}" from this tour? (It stays in the showroom database.)`)) return;
    try {
      await api.delete(`/admin/clients/${clientId}/stops/${s.id}`);
      if (editingStopId === s.id) resetForm();
      toast.success("Stop removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/t/${client.token}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Client link copied"))
      .catch(() => toast.info(url, { description: "Copy this link manually" }));
  };

  const center = useMemo(() => {
    if (client?.showrooms?.length) {
      const s = client.showrooms[0];
      return [s.lat, s.lng];
    }
    return [-6.2088, 106.8456];
  }, [client]);

  if (!client) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading tour…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="client-editor">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-8 py-5">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="back-to-dashboard">
              <Button variant="ghost" size="sm" className="rounded-full">
                <ArrowLeft className="mr-1 h-4 w-4" /> Clients
              </Button>
            </Link>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Client Tour
              </p>
              <h1 className="font-serif text-3xl font-light tracking-tighter" data-testid="editor-client-name">
                {client.name}
              </h1>
            </div>
          </div>
          <Button data-testid="copy-share-link" variant="outline" onClick={copyLink} className="rounded-full">
            <Copy className="mr-2 h-4 w-4" /> Copy client link
          </Button>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-8 py-10 md:grid-cols-12">
        <div className="space-y-8 md:col-span-5">
          <form onSubmit={save} className="rounded-2xl border border-border bg-card p-8" data-testid="stop-form">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-light tracking-tight">
                {editingStopId ? "Edit Stop" : "Add a Stop"}
              </h2>
              {editingStopId && (
                <Button type="button" variant="ghost" size="sm" onClick={resetForm} className="rounded-full" data-testid="cancel-edit-button">
                  <X className="mr-1 h-4 w-4" /> Cancel
                </Button>
              )}
            </div>

            <div className="mt-6 space-y-5">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Showroom
                </label>
                {editingStop ? (
                  <div
                    className="mt-2 flex items-center gap-3 rounded-xl border border-primary/30 bg-secondary/60 px-4 py-3"
                    data-testid="editing-showroom-name"
                  >
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                      {client.showrooms.findIndex((s) => s.id === editingStopId) + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-serif text-lg font-light">{editingStop.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        Editing checklist only — the showroom itself is managed in the database.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Select
                      value={selectedId}
                      onValueChange={(v) => {
                        setSelectedId(v);
                        const m = masters.find((x) => x.id === v);
                        if (m) setFocus([m.lat, m.lng]);
                      }}
                    >
                      <SelectTrigger data-testid="showroom-select" className="h-11 rounded-xl">
                        <SelectValue placeholder="Choose from the showroom database…" />
                      </SelectTrigger>
                      <SelectContent>
                        {available.length === 0 ? (
                          <SelectItem value="__none" disabled>
                            No showrooms left — add more in the database
                          </SelectItem>
                        ) : (
                          available.map((m) => (
                            <SelectItem key={m.id} value={m.id} data-testid={`showroom-option-${m.id}`}>
                              {m.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {available.length === 0 && !editingStopId && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    All showrooms are already in this tour.{" "}
                    <Link to="/admin/showrooms" className="font-semibold text-accent underline" data-testid="goto-showroom-database">
                      Manage the showroom database
                    </Link>
                  </p>
                )}
              </div>

              {shownShowroom && (
                <div className="rounded-xl border border-border bg-secondary/50 p-4" data-testid="selected-showroom-details">
                  <p className="font-serif text-lg font-light">{shownShowroom.name}</p>
                  {shownShowroom.address && (
                    <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
                      <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0" /> {shownShowroom.address}
                    </p>
                  )}
                  {shownShowroom.contact_name && (
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Phone className="h-3 w-3 flex-shrink-0" /> {shownShowroom.contact_name}
                      {shownShowroom.contact_phone ? ` · ${shownShowroom.contact_phone}` : ""}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Items to look at
                </label>
                <div className="mt-2 flex gap-2">
                  <Input
                    data-testid="category-input"
                    value={catInput}
                    onChange={(e) => setCatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addCategory();
                      }
                    }}
                    placeholder="e.g. Sofa fabric, Lighting…"
                    className="h-11 rounded-xl"
                  />
                  <Button data-testid="add-category-button" type="button" variant="outline" onClick={addCategory} className="h-11 rounded-xl px-4">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {categories.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="category-chips">
                    {categories.map((c) => (
                      <span key={c} className="flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1.5 text-xs font-medium">
                        {c}
                        <button
                          type="button"
                          data-testid={`remove-category-${c}`}
                          onClick={() => setCategories((cs) => cs.filter((x) => x !== c))}
                          className="text-muted-foreground transition-colors duration-150 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <Button
                data-testid="save-stop-button"
                type="submit"
                disabled={saving || (!editingStopId && !selectedId)}
                className="h-11 w-full rounded-full bg-primary text-primary-foreground transition-transform duration-200 hover:scale-[1.01] hover:bg-primary"
              >
                {saving ? "Saving…" : editingStopId ? "Update Checklist" : "Add to Tour"}
              </Button>
            </div>
          </form>

          <div className="space-y-3" data-testid="stop-list">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Route · {client.showrooms.length} stop{client.showrooms.length === 1 ? "" : "s"}
            </p>
            {client.showrooms.length === 0 && (
              <p className="rounded-2xl border border-dashed border-border bg-card p-8 text-sm text-muted-foreground">
                No stops yet. Pick a showroom from the dropdown above.
              </p>
            )}
            {client.showrooms.map((s, i) => (
              <div key={s.id} data-testid={`stop-row-${s.id}`} className="flex items-center justify-between rounded-2xl border border-border bg-card p-5">
                <div className="flex min-w-0 items-center gap-4">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-serif text-xl font-light">{s.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {s.address || "No address"} · {s.categories.length} item{s.categories.length === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
                <div className="ml-4 flex gap-1">
                  <Button data-testid={`edit-stop-${s.id}`} variant="ghost" size="sm" onClick={() => editStop(s)} className="rounded-full">
                    <PencilSimple className="h-4 w-4" />
                  </Button>
                  <Button data-testid={`delete-stop-${s.id}`} variant="ghost" size="sm" onClick={() => removeStop(s)} className="rounded-full text-muted-foreground hover:text-destructive">
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="sticky top-8 overflow-hidden rounded-2xl border border-border">
            <MapContainer center={center} zoom={12} className="h-[70vh] w-full" data-testid="admin-map">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FlyTo position={focus} />
              {client.showrooms.map((s, i) => (
                <Marker
                  key={s.id}
                  position={[s.lat, s.lng]}
                  icon={pinIcon(i + 1, { active: editingStopId === s.id })}
                  eventHandlers={{ click: () => editStop(s) }}
                />
              ))}
              {selected && !client.showrooms.some((s) => s.id === selected.id) && (
                <Marker position={[selected.lat, selected.lng]} icon={pinIcon("+", { draft: true })} />
              )}
            </MapContainer>
            <div className="flex items-center gap-2 border-t border-border bg-card px-5 py-3">
              <MapPin className="h-4 w-4 text-accent" />
              <p className="text-xs text-muted-foreground">
                Pins come from the showroom database. Click a pin to edit its checklist.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
